import { useCallback, useEffect, useRef } from 'react';
import type { RefObject } from 'react';
import type maplibregl from 'maplibre-gl';
import type { MapRef } from 'react-map-gl/maplibre';

import type { NetworkTableAreaRow, NetworkTableDmaRow, NetworkTablePoiRow } from '@/api/networkTableApi';
import type { NetworkTableActions } from '@/components/table-view/NetworkTablePanel';
import { areaLocateTarget, dmaLocateTarget, type LocateTarget } from '@/components/table-view/locateTarget';
import { tablePoiCoords } from '@/components/table-view/mapTablePoiToFeatureProperties';
import type { PoiFeatureProperties } from '@/components/map/utils/poiFeatureProperties';
import { POI_MIN_ZOOM } from '@/hooks/useEntitiesLayer';

/** Matches the DMA-search fit (useMapSearch). */
const FIT_PADDING = 80;
const FIT_DURATION = 600;
/**
 * Never zoom past the POI level when framing a group — a tiny DMA would otherwise fill the screen
 * at maximum zoom and lose all context.
 */
const FIT_MAX_ZOOM = 15;
/**
 * Last-resort safeguard for the pathological case where the map never reports a resize after the
 * layout is restored. The real signal is the `resize` event; this only stops a Locate being lost.
 */
const RESIZE_FALLBACK_MS = 500;

interface UseNetworkTableActionsParams {
    mapRef: RefObject<MapRef | null>;
    /** True when the table is covering the map, so Locate must reveal it before navigating. */
    isMapHidden: boolean;
    /**
     * Brings the map back on screen. The caller decides how — the 50/50 split on desktop, closing
     * the table on mobile, where a full-screen table is the deliberate design and there is no split
     * to restore. Locate only needs to know that afterwards the map is visible and will resize.
     */
    revealMap: () => void;
    /** Id of the provider-located POI, passed back so the table can tint the active row. */
    locatedPoiId: string | null;
    /**
     * Map-interaction commands. Locates are highlight-only; showPoiDetails opens the details
     * panel. `null` means "locate the Unassigned bucket": nothing to highlight, but the locate
     * must still replace any previous located feature and close an open details panel.
     */
    locatePoi: (id: string) => void;
    locateArea: (name: string | null) => void;
    locateDma: (ident: string | null) => void;
    showPoiDetails: (poi: PoiFeatureProperties, coords: [number, number]) => void;
}

/**
 * The map side of the Network Overview table's row actions.
 *
 * Locate = navigate + highlight, and nothing else. It never opens the POI details panel and never
 * sets `selection` (which would open the stats card) — those states stay under the map's own click
 * paths and Show Details. While the table covers the map it is `display:none`, so Locate first
 * reveals the map (`revealMap`), waits for it to actually resize, and only then navigates —
 * navigating a hidden map would compute against a zero-size container.
 */
export function useNetworkTableActions({
    mapRef,
    isMapHidden,
    revealMap,
    locatedPoiId,
    locatePoi,
    locateArea,
    locateDma,
    showPoiDetails,
}: UseNetworkTableActionsParams): NetworkTableActions {
    // Monotonic token: only the newest pending navigation may run, so rapid Locates cannot
    // fire a stale fly-to after a newer one.
    const tokenRef = useRef(0);

    /**
     * Tears down whatever a pending navigation is holding — its `resize` listener and its fallback
     * timer, together. ONE ref for both, because they are two halves of a single wait and must be
     * released as a unit: releasing only the timer (as this used to) leaves the listener attached to
     * the map indefinitely, and it goes on to clear the fallback of a LATER Locate, which then never
     * navigates if its own resize does not arrive. Superseding and unmounting both go through here.
     */
    const pendingTeardownRef = useRef<(() => void) | null>(null);

    const cancelPending = useCallback(() => {
        pendingTeardownRef.current?.();
        pendingTeardownRef.current = null;
    }, []);

    useEffect(() => {
        return () => {
            cancelPending();
            // Abandon any pending navigation on unmount.
            tokenRef.current += 1;
        };
    }, [cancelPending]);

    const applyTarget = useCallback((map: maplibregl.Map, target: LocateTarget) => {
        if (target.type === 'point') {
            // Zoom in to at least the POI level, but never back out if already closer.
            map.easeTo({ center: target.center, zoom: Math.max(map.getZoom(), POI_MIN_ZOOM) });
            return;
        }
        map.fitBounds(target.bounds, { padding: FIT_PADDING, duration: FIT_DURATION, maxZoom: FIT_MAX_ZOOM });
    }, []);

    /**
     * Reveals the map first when it is hidden, then navigates once it has genuinely resized.
     * Sequence: revealMap → React commits → the container's ResizeObserver calls map.resize() →
     * MapLibre emits `resize` → we navigate. The timeout is only a safeguard.
     */
    const navigate = useCallback(
        (target: LocateTarget) => {
            const token = ++tokenRef.current;
            // Release the previous wait BEFORE starting a new one — including on the already-visible
            // path below, which used to return early and strand the old listener.
            cancelPending();

            const run = () => {
                if (token !== tokenRef.current) return; // superseded by a newer Locate
                const map = mapRef.current?.getMap();
                if (!map) return;
                applyTarget(map, target);
            };

            if (!isMapHidden) {
                // The map is already visible — nothing to reveal, no waiting.
                run();
                return;
            }

            revealMap();

            const map = mapRef.current?.getMap();
            if (!map) return;

            // `on`/`off` rather than `once`: whichever of the resize and the fallback fires first
            // tears the other down through the shared teardown, so a Locate can never navigate
            // twice and never leaves half of its wait attached to the map.
            const onResize = () => {
                cancelPending();
                run();
            };
            const timer = setTimeout(() => {
                cancelPending();
                run();
            }, RESIZE_FALLBACK_MS);

            map.on('resize', onResize);
            pendingTeardownRef.current = () => {
                map.off('resize', onResize);
                clearTimeout(timer);
            };
        },
        [applyTarget, cancelPending, isMapHidden, mapRef, revealMap],
    );

    /**
     * Locate highlights exactly one thing. The interaction provider enforces that structurally:
     * LOCATE_FEATURE replaces the whole located record (so an earlier Locate of ANY type is
     * cleared) and clears the selection (so an open details panel or stats card closes rather
     * than disagreeing with the new highlight).
     */
    const onLocatePoi = useCallback(
        (poi: NetworkTablePoiRow) => {
            // Highlight only. Deliberately NOT showPoiDetails(), which would open the panel.
            // The highlight targets the server-supplied map feature id: a child row's marker
            // is its 'leak:'-prefixed leak feature, so the raw row id would silently match
            // nothing on the map.
            locatePoi(poi.mapEntityId ?? poi.id);
            navigate({ type: 'point', center: tablePoiCoords(poi) });
        },
        [locatePoi, navigate],
    );

    const onLocateArea = useCallback(
        (area: NetworkTableAreaRow) => {
            const target = areaLocateTarget(area);
            if (target === null) return;
            // The provider records every locate (clearing any previous located feature and
            // closing an open details panel). The Unassigned bucket is not a real area — there
            // is no polygon to highlight (null), so its POIs are simply framed.
            locateArea(area.isUnassigned ? null : area.name);
            navigate(target);
        },
        [locateArea, navigate],
    );

    const onLocateDma = useCallback(
        (dma: NetworkTableDmaRow) => {
            const target = dmaLocateTarget(dma);
            if (target === null) return;
            // Follows the DMA-search precedent exactly: highlight + fit, no stats card.
            locateDma(dma.isUnassigned ? null : dma.ident);
            navigate(target);
        },
        [locateDma, navigate],
    );

    const onShowPoiDetails = useCallback(
        (poi: PoiFeatureProperties, coords: [number, number]) => {
            // Details supersedes a Locate highlight: SELECT_FEATURE clears `located` atomically,
            // so the halo transfers to the selection and the two sources can never both be live.
            showPoiDetails(poi, coords);
        },
        [showPoiDetails],
    );

    return { onLocatePoi, onLocateArea, onLocateDma, onShowPoiDetails, locatedPoiId };
}
